import { ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface WizardStep {
  id: string;
  title: string;
  shortTitle?: string;
  icon?: ReactNode;
  validate?: () => string | null; // return error message or null
  render: () => ReactNode;
}

interface Props {
  steps: WizardStep[];
  onSubmit: () => Promise<void> | void;
  submitting?: boolean;
  submitLabel?: string;
  title: string;
  subtitle?: string;
  topIcon?: ReactNode;
}

export function RegistrationWizard({ steps, onSubmit, submitting, submitLabel = 'Submit Application', title, subtitle, topIcon }: Props) {
  const [active, setActive] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const last = active === steps.length - 1;
  const step = steps[active];

  const next = () => {
    const err = step.validate?.();
    if (err) { setError(err); return; }
    setError(null);
    setActive(a => Math.min(a + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const back = () => { setError(null); setActive(a => Math.max(a - 1, 0)); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const submit = async () => {
    const err = step.validate?.();
    if (err) { setError(err); return; }
    setError(null);
    await onSubmit();
  };

  return (
    <div className="container max-w-4xl px-3 md:px-6 py-5 md:py-10 pb-24 md:pb-10">
      <div className="text-center mb-5 md:mb-6">
        {topIcon}
        <h1 className="font-display text-xl md:text-3xl font-bold mt-2">{title}</h1>
        {subtitle && <p className="text-muted-foreground text-xs md:text-sm mt-2 max-w-xl mx-auto">{subtitle}</p>}
      </div>

      {/* Stepper */}
      <div className="mb-8">
        <div className="flex items-center justify-between gap-1 overflow-x-auto scrollbar-hide pb-2">
          {steps.map((s, i) => {
            const done = i < active;
            const current = i === active;
            return (
              <button
                type="button"
                key={s.id}
                onClick={() => i < active && setActive(i)}
                className={cn(
                  'flex items-center gap-2 shrink-0 text-xs md:text-sm transition-colors',
                  i < active ? 'text-primary cursor-pointer' : current ? 'text-foreground font-semibold' : 'text-muted-foreground',
                )}
              >
                <span className={cn(
                  'h-7 w-7 rounded-full border-2 flex items-center justify-center text-xs font-bold shrink-0',
                  done ? 'bg-primary border-primary text-primary-foreground' :
                  current ? 'border-primary text-primary' : 'border-muted-foreground/40'
                )}>
                  {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className="hidden md:inline whitespace-nowrap">{s.shortTitle || s.title}</span>
                {i < steps.length - 1 && <span className="hidden md:block w-6 h-px bg-muted-foreground/30 mx-1" />}
              </button>
            );
          })}
        </div>
        <div className="md:hidden mt-2 text-center text-sm font-semibold">{step.title} <span className="text-muted-foreground font-normal">— Step {active + 1} of {steps.length}</span></div>
      </div>

      {/* Step content */}
      <div className="rounded-xl border bg-card p-4 md:p-7 shadow-sm">
        <div className="hidden md:flex items-center gap-2 mb-5 pb-4 border-b">
          {step.icon}
          <h2 className="text-lg font-bold">{step.title}</h2>
        </div>
        {step.render()}
        {error && (
          <div className="mt-4 text-sm text-destructive bg-destructive/10 border border-destructive/30 px-3 py-2 rounded-md">
            {error}
          </div>
        )}
      </div>

      {/* Nav (sticky on mobile) */}
      <div className="fixed bottom-0 left-0 right-0 z-30 md:static bg-background md:bg-transparent border-t md:border-0 px-3 md:px-0 py-3 md:py-0 md:mt-5 flex justify-between gap-3" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
        <Button variant="outline" onClick={back} disabled={active === 0 || submitting} className="gap-1 h-11 md:h-10">
          <ChevronLeft className="h-4 w-4" /> Back
        </Button>
        {last ? (
          <Button onClick={submit} disabled={submitting} className="gap-1 flex-1 md:flex-initial md:min-w-[180px] h-11 md:h-10">
            {submitting ? 'Submitting...' : submitLabel}
          </Button>
        ) : (
          <Button onClick={next} className="gap-1 flex-1 md:flex-initial h-11 md:h-10">
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

/* ---------- Reusable chip multi-select ---------- */
export function ChipMultiSelect({
  options, value, onChange,
}: { options: string[]; value: string[]; onChange: (v: string[]) => void }) {
  const toggle = (opt: string) => {
    onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const on = value.includes(opt);
        return (
          <button
            type="button" key={opt} onClick={() => toggle(opt)}
            className={cn(
              'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
              on ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground hover:border-primary/50',
            )}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Free-form chip input (areas of operation) ---------- */
export function ChipInput({ value, onChange, placeholder }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (v && !value.includes(v) && value.length < 30) onChange([...value, v]);
    setDraft('');
  };
  return (
    <div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
          placeholder={placeholder || 'Type and press Enter'}
          className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
        <Button type="button" variant="outline" onClick={add}>Add</Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {value.map(v => (
            <span key={v} className="px-3 py-1 rounded-full text-xs bg-primary/10 text-primary flex items-center gap-1.5">
              {v}
              <button type="button" onClick={() => onChange(value.filter(x => x !== v))} className="hover:text-destructive">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
